import React, { useState } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  ButtonGroup
} from "@material-ui/core";
import {
  Search as SearchIcon,
  GetApp as ExportIcon,
  Cake as CakeIcon
} from "@material-ui/icons";
import api from "../../services/api";
import * as XLSX from "xlsx";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3)
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  searchBox: {
    marginBottom: theme.spacing(3)
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    maxHeight: 600
  },
  table: {
    minWidth: 650
  },
  searchButton: {
    background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)",
    color: "white",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    marginLeft: theme.spacing(2),
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)"
    }
  },
  exportButton: {
    background: "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
    color: "white",
    fontWeight: 600,
    textTransform: "none",
    borderRadius: 8,
    padding: theme.spacing(1, 3),
    marginTop: theme.spacing(2),
    "&:hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)"
    }
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  },
  ageChip: {
    backgroundColor: theme.palette.primary.main,
    color: "white"
  }
}));

const BirthdaySearch = () => {
  const classes = useStyles();
  const [searchType, setSearchType] = useState("date"); // "date" ou "month"
  const [searchDate, setSearchDate] = useState("");
  const [searchMonth, setSearchMonth] = useState("");
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (searchType === "date" && !searchDate) {
      toast.error("Por favor, selecione uma data");
      return;
    }

    if (searchType === "month" && !searchMonth) {
      toast.error("Por favor, selecione um mês");
      return;
    }

    setLoading(true);
    setHasSearched(true);
    
    try {
      const params = {};
      
      if (searchType === "date") {
        // Converter data do formato YYYY-MM-DD para DD/MM/YYYY
        const [year, month, day] = searchDate.split('-');
        params.date = `${day}/${month}/${year}`;
      } else {
        // Mês já está no formato YYYY-MM
        params.month = searchMonth;
      }
      
      const { data } = await api.get("/birthdays/by-date", { params });
      
      setContacts(data.data || []);
      
      if (data.data && data.data.length === 0) {
        const message = searchType === "date" 
          ? "Nenhum contato encontrado para esta data"
          : "Nenhum contato encontrado para este mês";
        toast.info(message);
      } else {
        toast.success(`${data.count || 0} contato(s) encontrado(s)`);
      }
    } catch (error) {
      console.error("Erro ao buscar aniversariantes:", error);
      toast.error("Erro ao buscar aniversariantes");
      setContacts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleExportToExcel = () => {
    if (contacts.length === 0) {
      toast.error("Não há dados para exportar");
      return;
    }

    try {
      // Preparar dados para exportação
      const exportData = contacts.map((contact, index) => ({
        "#": index + 1,
        "Nome": contact.name || "",
        "Número": contact.number || "",
        "Email": contact.email || "",
        "Data de Nascimento": contact.birthDate || "",
        "Idade": contact.age || "N/A"
      }));

      // Criar workbook e worksheet
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Ajustar largura das colunas
      const colWidths = [
        { wch: 5 },   // #
        { wch: 30 },  // Nome
        { wch: 20 },  // Número
        { wch: 30 },  // Email
        { wch: 18 },  // Data de Nascimento
        { wch: 10 }   // Idade
      ];
      ws['!cols'] = colWidths;

      // Adicionar worksheet ao workbook
      XLSX.utils.book_append_sheet(wb, ws, "Aniversariantes");

      // Gerar nome do arquivo
      const fileName = searchType === "date"
        ? `Aniversariantes_${searchDate.replace(/-/g, '_')}.xlsx`
        : `Aniversariantes_${searchMonth.replace(/-/g, '_')}.xlsx`;

      // Fazer download do arquivo
      XLSX.writeFile(wb, fileName);

      toast.success(`Arquivo "${fileName}" exportado com sucesso!`);
    } catch (err) {
      console.error("Erro ao exportar para Excel:", err);
      toast.error("Erro ao exportar para Excel");
    }
  };

  const formatPhoneNumber = (number) => {
    if (!number) return "";
    
    if (number.length > 15) {
      return `Grupo (${number.substring(0, 6)}...)`;
    }
    
    if (number.length === 12 && number.startsWith('55')) {
      const ddd = number.substring(2, 4);
      const firstPart = number.substring(4, 9);
      const lastPart = number.substring(9);
      return `+55 (${ddd}) ${firstPart}-${lastPart}`;
    }
    
    return number;
  };

  return (
    <Box className={classes.root}>
      <Typography variant="h5" gutterBottom style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CakeIcon />
        Buscar Aniversariantes
      </Typography>

      <Paper className={classes.paper}>
        <Box className={classes.searchBox}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12}>
              <Box mb={2}>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Tipo de busca:
                </Typography>
                <ButtonGroup
                  variant="contained"
                  color="primary"
                  aria-label="tipo de busca"
                >
                  <Button
                    variant={searchType === "date" ? "contained" : "outlined"}
                    onClick={() => {
                      setSearchType("date");
                      setSearchDate("");
                      setSearchMonth("");
                      setContacts([]);
                      setHasSearched(false);
                    }}
                    style={{
                      backgroundColor: searchType === "date" ? undefined : "transparent",
                      color: searchType === "date" ? undefined : "inherit"
                    }}
                  >
                    Por Data
                  </Button>
                  <Button
                    variant={searchType === "month" ? "contained" : "outlined"}
                    onClick={() => {
                      setSearchType("month");
                      setSearchDate("");
                      setSearchMonth("");
                      setContacts([]);
                      setHasSearched(false);
                    }}
                    style={{
                      backgroundColor: searchType === "month" ? undefined : "transparent",
                      color: searchType === "month" ? undefined : "inherit"
                    }}
                  >
                    Por Mês
                  </Button>
                </ButtonGroup>
              </Box>
            </Grid>
            <Grid item xs={12} md={searchType === "date" ? 4 : 6}>
              {searchType === "date" ? (
                <TextField
                  type="date"
                  label="Data de Aniversário"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="Selecione a data para buscar aniversariantes"
                />
              ) : (
                <TextField
                  type="month"
                  label="Mês de Aniversário"
                  value={searchMonth}
                  onChange={(e) => setSearchMonth(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  helperText="Selecione o mês para buscar todos os aniversariantes"
                />
              )}
            </Grid>
            <Grid item xs={12} md={searchType === "date" ? 8 : 6}>
              <Button
                variant="contained"
                className={classes.searchButton}
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={handleSearch}
                disabled={loading || (searchType === "date" ? !searchDate : !searchMonth)}
              >
                {loading ? "Buscando..." : "Buscar"}
              </Button>
            </Grid>
          </Grid>
        </Box>

        {hasSearched && (
          <>
            {contacts.length > 0 && (
              <>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">
                    {contacts.length} contato(s) encontrado(s)
                  </Typography>
                  <Button
                    variant="contained"
                    className={classes.exportButton}
                    startIcon={<ExportIcon />}
                    onClick={handleExportToExcel}
                  >
                    Exportar para Excel
                  </Button>
                </Box>

                <TableContainer className={classes.tableContainer}>
                  <Table className={classes.table} stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>Nome</TableCell>
                        <TableCell>Número</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Data de Nascimento</TableCell>
                        <TableCell>Idade</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {contacts.map((contact, index) => (
                        <TableRow key={contact.id} hover>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>{contact.name}</TableCell>
                          <TableCell>{formatPhoneNumber(contact.number)}</TableCell>
                          <TableCell>{contact.email || "-"}</TableCell>
                          <TableCell>{contact.birthDate}</TableCell>
                          <TableCell>
                            <Chip
                              label={contact.age ? `${contact.age} ano(s)` : "N/A"}
                              size="small"
                              className={classes.ageChip}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}

            {contacts.length === 0 && !loading && (
              <Box className={classes.emptyState}>
                <CakeIcon style={{ fontSize: 64, opacity: 0.3, marginBottom: 16 }} />
                <Typography variant="h6" color="textSecondary">
                  {searchType === "date" 
                    ? "Nenhum contato encontrado para esta data"
                    : "Nenhum contato encontrado para este mês"}
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                  {searchType === "date" 
                    ? "Tente selecionar outra data"
                    : "Tente selecionar outro mês"}
                </Typography>
              </Box>
            )}
          </>
        )}

        {!hasSearched && (
          <Box className={classes.emptyState}>
            <CakeIcon style={{ fontSize: 64, opacity: 0.3, marginBottom: 16 }} />
            <Typography variant="h6" color="textSecondary">
              {searchType === "date" 
                ? "Selecione uma data para buscar aniversariantes"
                : "Selecione um mês para buscar aniversariantes"}
            </Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default BirthdaySearch;
